import { Fragment } from 'react';
import {
  canonicalGuaranteedOverride,
  isGuaranteedOverride,
  type GuaranteedServiceOrgSkuEntry,
  type PatchGuaranteedServiceRequest,
} from '@nadoutong/contracts';
import { Trash2 } from 'lucide-react';
import { CustomerCell } from '../../components/identity/CustomerCell.tsx';
import { useT } from '../../i18n/index.ts';
import { IconButton, Select, Table, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';
import { fallbackLabelKey, MODE_VALUES, overlayFallback } from './modes.ts';
import { RowWriteStatus } from './RowWriteStatus.tsx';
import type { WriteOutcome } from './useGuaranteedWrites.ts';
import { isRowBusy, pendingField, type RowWriteStatus as RowStatus } from './writeStatus.ts';

function countOf(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function OrgOverlayTable({
  rows,
  customerName,
  customerEmail,
  statusFor,
  overlayCount,
  onPatch,
  onEditNote,
  onDelete,
  onOpenGuard,
  guardDisabled = false,
}: {
  readonly rows: readonly GuaranteedServiceOrgSkuEntry[];
  readonly customerName: (orgId: string) => string;
  readonly customerEmail: (orgId: string) => string;
  readonly statusFor: (id: string) => RowStatus | undefined;
  readonly overlayCount: (skuId: string, orgId: string) => number;
  readonly onPatch: (row: GuaranteedServiceOrgSkuEntry, body: PatchGuaranteedServiceRequest) => Promise<WriteOutcome>;
  readonly onEditNote: (id: string, note: string) => void;
  readonly onDelete: (id: string) => void;
  readonly onOpenGuard: (skuId: string, orgId: string) => void;
  readonly guardDisabled?: boolean;
}): JSX.Element {
  const t = useT();
  const options = [
    { value: 'inherit', label: t('guaranteed.inherit') },
    ...MODE_VALUES.map((value) => ({ value, label: t(fallbackLabelKey(value)) })),
  ];
  return (
    <Table className="rounded-none border-0 bg-transparent" testId="guaranteed-overlay-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('guaranteed.customer')}</Th>
          <Th>{t('guaranteed.sku')}</Th>
          <Th>{t('guaranteed.overlay.fallback')}</Th>
          <Th>{t('guaranteed.col.guard')}</Th>
          <Th num className="whitespace-nowrap">{t('guaranteed.overlay.retries')}</Th>
          <Th>{t('guaranteed.overlay.note')}</Th>
          <Th>{t('guaranteed.delete')}</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map((row) => {
          const status = statusFor(row.id);
          const busy = isRowBusy(status);
          const fallback = canonicalGuaranteedOverride(pendingField(status, 'ultimate_fallback')) ?? overlayFallback(row);
          const email = customerEmail(row.org_id);
          const sub = email.length > 0 ? `${row.org_id} · ${email}` : row.org_id;
          return (
            <Fragment key={row.id}>
            <Tr data-testid={`guaranteed-overlay-${row.id}`} aria-busy={busy || undefined}>
              <Td>
                <CustomerCell
                  orgId={row.org_id}
                  testId={`guaranteed-overlay-customer-${row.id}`}
                  secondary={
                    <div>
                      <span className="block max-w-[15rem] truncate font-medium text-paper" title={customerName(row.org_id)}>
                        {customerName(row.org_id)}
                      </span>
                      <span className="block max-w-[15rem] truncate font-mono text-micro text-paper-low" title={sub}>{sub}</span>
                    </div>
                  }
                />
              </Td>
              <Td>
                <span className="block truncate" title={row.sku_name}>{row.sku_name}</span>
              </Td>
              <Td>
                <Select
                  value={fallback}
                  disabled={busy}
                  onValueChange={(value) => {
                    if (!isGuaranteedOverride(value) || value === 'on' || value === fallback) return;
                    void onPatch(row, { ultimate_fallback: value });
                  }}
                  options={options}
                  ariaLabel={t('guaranteed.overlay.fallback')}
                  testId={`guaranteed-fallback-${row.id}`}
                />
              </Td>
              <Td>
                <button
                  type="button"
                  className="text-left text-dense text-paper hover:underline disabled:pointer-events-none"
                  disabled={busy || guardDisabled}
                  data-testid={`guaranteed-guard-overlay-${row.id}`}
                  onClick={() => onOpenGuard(row.sku_id, row.org_id)}
                >
                  {t('guaranteed.guard.overlay_fields', { n: String(overlayCount(row.sku_id, row.org_id)) })}
                </button>
              </Td>
              <Td num className="whitespace-nowrap">
                <span data-testid={`guaranteed-overlay-24h-${row.id}`}>
                  <span>{countOf(row.entered_chain_count_24h)}</span>
                  {' / '}
                  <span>{countOf(row.request_count_24h)}</span>
                  {' '}
                  {t('guaranteed.overlay.requests')}
                </span>
              </Td>
              <Td>
                <button
                  type="button"
                  className="block max-w-full truncate text-left text-label text-paper-mid"
                  title={row.note ?? ''}
                  data-testid={`guaranteed-note-${row.id}`}
                  disabled={busy}
                  onClick={() => onEditNote(row.id, row.note ?? '')}
                >
                  {row.note ?? t('guaranteed.note_placeholder')}
                </button>
              </Td>
              <Td>
                <IconButton
                  label={t('guaranteed.delete')}
                  size="sm"
                  className="hover:text-bad"
                  data-testid={`guaranteed-delete-${row.id}`}
                  disabled={busy}
                  onClick={() => onDelete(row.id)}
                >
                  <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                </IconButton>
              </Td>
            </Tr>
            {status !== undefined ? (
              <Tr interactive={false}>
                <Td colSpan={7} className="pt-0">
                  <RowWriteStatus status={status} testId={`guaranteed-write-status-${row.id}`} />
                </Td>
              </Tr>
            ) : null}
            </Fragment>
          );
        })}
      </TBody>
    </Table>
  );
}
